package com.royceecoleman.bill.shared.aichat

import kotlinx.serialization.Serializable

const val AI_CHAT_USER_ID_HEADER = "X-Bill-User-Id"
const val AI_CHAT_SERVICE_AUTH_HEADER = "X-Bill-Service-Token"

@Serializable
enum class ToolProfile {
    GENERAL,
}

@Serializable
data class AiModelDto(
    val id: String,
    val name: String,
    val description: String,
    val isDefault: Boolean,
)

@Serializable
data class AiChatSessionDto(
    val id: Long,
    val name: String,
    val startTime: Long,
    val model: String,
    val toolProfile: String,
    val ownerId: Int,
    val ticketProjectId: String? = null,
)

@Serializable
data class ChatTurnDto(
    val turnIndex: Int,
    val timestamp: Long,
    val sender: String,
    val content: String,
    val sessionId: Long,
)

@Serializable
data class CreateSessionRequest(
    val name: String? = null,
    val model: String? = null,
    val toolProfile: ToolProfile = ToolProfile.GENERAL,
    val ticketProjectId: String? = null,
)

@Serializable
data class UpdateSessionModelRequest(
    val modelId: String,
)

@Serializable
data class SendMessageRequest(
    val text: String,
)

@Serializable
data class SendMessageResponse(
    val session: AiChatSessionDto,
    val userTurn: ChatTurnDto,
    val assistantTurn: ChatTurnDto,
)

@Serializable
enum class ManagedAiModelType {
    CHAT,
}

@Serializable
data class ManagedAiModelDto(
    val id: String,
    val type: ManagedAiModelType = ManagedAiModelType.CHAT,
    val name: String,
    val provider: String = "",
    val deploymentName: String = "",
    val temperature: Double = 1.0,
    val supportedFrameworks: List<String> = emptyList(),
    val isDefault: Boolean = false,
    val deprecated: Boolean = false,
)

@Serializable
data class ManagedAiModelUpdateRequest(
    val id: String,
    val type: ManagedAiModelType = ManagedAiModelType.CHAT,
    val name: String,
    val provider: String = "",
    val deploymentName: String = "",
    val temperature: Double = 1.0,
    val supportedFrameworks: List<String> = listOf(SPRING_AI_FRAMEWORK),
)

const val SPRING_AI_FRAMEWORK = "SPRING_AI"

/** Providers bill-ai can serve through Spring AI. Keep in sync with AppAiProperties.Provider. */
val BILL_SUPPORTED_MODEL_PROVIDERS: List<String> = listOf("OPENROUTER")

fun List<ManagedAiModelDto>.visibleManagedModels(showDeprecated: Boolean): List<ManagedAiModelDto> =
    if (showDeprecated) this else filterNot { it.deprecated }

fun List<ManagedAiModelDto>.sortedForManagement(): List<ManagedAiModelDto> =
    sortedWith(compareByDescending<ManagedAiModelDto> { it.isDefault }.thenBy { it.name.lowercase() }.thenBy { it.id })

/** True when the model may be deleted or deprecated from the admin UI. */
fun ManagedAiModelDto.canBeRemoved(): Boolean = !isDefault

fun validateManagedModelRequest(request: ManagedAiModelUpdateRequest): String? =
    when {
        request.id.trim().isEmpty() -> "Model id is required."
        !request.id.trim().matches(MANAGED_MODEL_ID_PATTERN) -> "Model id contains unsupported characters."
        request.name.isBlank() -> "Model name is required."
        request.provider.trim().uppercase() !in BILL_SUPPORTED_MODEL_PROVIDERS ->
            "Provider must be one of ${BILL_SUPPORTED_MODEL_PROVIDERS.joinToString(", ")}."
        request.deploymentName.isBlank() -> "Deployment name is required."
        !request.temperature.isFinite() -> "Temperature must be finite."
        request.supportedFrameworks.none { it.trim().uppercase() == SPRING_AI_FRAMEWORK } ->
            "Bill chat models must declare $SPRING_AI_FRAMEWORK support."
        else -> null
    }

private val MANAGED_MODEL_ID_PATTERN = Regex("[A-Za-z0-9._:-]+")
